// ReportModal.tsx — the five-category report dialog (platform "heart"), and the
// legal-floor track that is deliberately NOT part of it.
//
// Platform rule: FIVE and ONLY FIVE moderation categories; posts stay visible
// during review.
//
// The legal floor is a MODE, never a sixth radio. The Council's remit is
// judgement — five kinds of harm, weighed by people, voted on, appealable.
// Material that is unlawful to host is the opposite: there is nothing to weigh,
// and a vote would be meaningless because the platform has no lawful option to
// keep it up (see LegalFloorCategory's docstring in the moderation models). So
// the two lists never appear on screen together: choosing the legal floor
// REPLACES the five rather than joining them. Rendering it as a sixth option
// would make "exactly five" a lie in the one place a member is looking at the
// list.
//
// It also changes where the report goes, which is the whole point:
//   five categories → useCases().createCaseFromReport / createAccountCaseFromReport
//                     → opens a Case, subject to the weight/agreement gate.
//   legal floor     → legalFloorService.report() → creates NO case and applies
//                     NO gate, because the gate exists to stop mass-reporting
//                     silencing speech and one report of this kind must reach a
//                     person.
// The Terms and the Community Guidelines both already promised members "a
// reason for this track" in the report flow. This is that reason.

import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import styled from 'styled-components';
import { Dialog, Button, Textarea, useToast, Avatar, Callout } from '@/ui';
import { Icon } from '@/icons';
import { useCases } from '@/hooks';
import { MODERATION_CATEGORIES, CATEGORY_LABELS } from '@/core/models';
import type { ModerationCategory } from '@/core/models';
import type { Post, User } from '@/core/models';
import { bodyToPlainText } from '@/components/PostCard/bodyText';
import { ApiError } from '@/core/lib/apiClient';
import {
  legalFloorService,
  LEGAL_FLOOR_CATEGORIES,
  LEGAL_FLOOR_DETAIL_MAX,
  type LegalFloorCategoryId,
} from '@/core/services/legalFloorService';

// ─── Props ─────────────────────────────────────────────────────────────────────

export type ReportTarget =
  | { kind: 'post'; post: Post }
  | { kind: 'account'; user: User };

export interface ReportModalProps {
  target: ReportTarget;
  onClose: () => void;
}

/** Which track the dialog is currently offering. The two are never both on screen. */
type Track = 'council' | 'legal-floor';

// ─── Styled components ─────────────────────────────────────────────────────────

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PostPreview = styled.div`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
`;

const PreviewAuthor = styled.p`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const PreviewHandle = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: 6px;
`;

const PreviewBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: pre-wrap;
  word-break: break-word;
`;

// ─── Account preview styled pieces ────────────────────────────────────────────

const AccountPreview = styled.div`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AccountPreviewInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AccountPreviewName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AccountPreviewHandle = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 400;
  color: ${({ theme }) => theme.color.textMuted};
`;

// ─── Shared styled pieces ──────────────────────────────────────────────────────

const SectionLabel = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.color.textMuted};
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 9px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  transition: border-color 120ms ease, background 120ms ease;

  &:has(input:checked) {
    background: ${({ theme }) =>
      `color-mix(in srgb, ${theme.color.accentCyan} 14%, ${theme.color.glassBg})`};
  }

  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
`;

const RadioInput = styled.input`
  accent-color: ${({ theme }) => theme.color.accentCyan};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const DecorativeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0.5;
`;

const DecorativeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const DecorativeBadge = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 1px 6px;
  margin-left: auto;
`;

const InfoBox = styled.div`
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 12px 14px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const InfoText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.5;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

// ─── Legal-floor styled pieces ────────────────────────────────────────────────

/* Deliberately not shaped like a category row. It is an exit from the list, not
   an entry in it — a dashed rule and its own button, so nobody reads it as the
   sixth thing they could tick. */
const EscapeHatch = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px dashed ${({ theme }) => theme.color.glassBorder};
  /* LONGHAND, and glass-aware in both halves: glass OFF is fully opaque. */
  background-color: ${({ theme }) =>
    theme.cardsGlass ? theme.color.glassBg : theme.color.surface};
`;

const EscapeText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Intro = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

const LegalRadioLabel = styled(RadioLabel)`
  align-items: flex-start;
`;

const RadioText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RadioHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* Errors carry a word, never a colour alone: the danger hue is decoration on
   top of text that says what is wrong. */
const FieldError = styled.p`
  margin: -8px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

const FieldHint = styled.p`
  margin: -8px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  color: ${({ theme }) => theme.color.textMuted};
`;

/* The box stops dead at the limit. Without this it stops silently, and the
   reporter cannot tell whether the rest of what they typed was sent. */
const Counter = styled.p`
  margin: -8px 0 0;
  align-self: flex-end;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textFaint};
`;

/* Focused programmatically when a send fails (see the two form-error effects),
   so tabIndex={-1} is set at the call sites. The outline would otherwise draw
   around a box nobody clicked. */
const FormError = styled(Callout)`
  display: flex;
  gap: 10px;
  align-items: flex-start;

  &:focus {
    outline: none;
  }
`;

const Receipt = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Focused programmatically once the report lands (see the receipt effect).
     The outline would otherwise draw around a box nobody clicked. */
  &:focus {
    outline: none;
  }
`;

const Note = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + '…' : s;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const PREVIEW_MAX = 140;

// The same words /report-illegal uses, on purpose: someone who bounces off one
// surface and tries the other should not be told two different things.
const LEGAL_CATEGORY_ERROR = 'Choose which of the three this is, so it reaches the right person.';
const LEGAL_DETAIL_ERROR = 'Tell us what you found and where. We cannot act on a blank report.';
/** Shared by BOTH tracks: a send that did not land reads the same either side. */
const SEND_ERROR =
  'We could not send that report. Please try again, and if it keeps failing, that is our fault, not yours.';

export function ReportModal({ target, onClose }: ReportModalProps) {
  const [track, setTrack] = useState<Track>('council');

  const [selected, setSelected] = useState<ModerationCategory | null>(null);
  const [context, setContext] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [legalCategory, setLegalCategory] = useState<LegalFloorCategoryId | null>(null);
  const [legalDetail, setLegalDetail] = useState('');
  const [legalCategoryError, setLegalCategoryError] = useState<string | null>(null);
  const [legalDetailError, setLegalDetailError] = useState<string | null>(null);
  const [legalFormError, setLegalFormError] = useState<string | null>(null);
  const [legalSent, setLegalSent] = useState(false);
  // The Council track's equivalent. Its submit used to be try/finally with no
  // catch: a rejected createCase escaped as an unhandled rejection, no toast
  // fired, the dialog never closed, and the reporter was left with a live button
  // and no way to tell whether their report had gone.
  const [councilFormError, setCouncilFormError] = useState<string | null>(null);

  const { createCaseFromReport, createAccountCaseFromReport } = useCases();
  const toast = useToast();

  // A bounce that only paints red leaves a keyboard or screen-reader user parked
  // on the Send button with no idea which control it was talking about.
  const legalFirstRadio = useRef<HTMLInputElement>(null);
  const legalDetailField = useRef<HTMLTextAreaElement>(null);

  // The receipt replaces the whole form, so focus has to follow it — otherwise
  // the button someone just pressed vanishes underneath them, silently.
  const receiptRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (legalSent) receiptRef.current?.focus();
  }, [legalSent]);

  // Same treatment for the failure the reporter most needs to notice: the box
  // appears below a button they have just pressed, so without moving focus a
  // screen-reader user is told nothing and may reasonably assume it sent.
  // role="alert" announces it; focus is what lets them find and re-read it.
  const legalFormErrorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (legalFormError) legalFormErrorRef.current?.focus();
  }, [legalFormError]);

  const councilFormErrorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (councilFormError) councilFormErrorRef.current?.focus();
  }, [councilFormError]);

  // Switching tracks unmounts the very control that was pressed, which drops a
  // keyboard user onto <body> and makes them tab in from the top of the dialog.
  // Skipped on first render, where the Dialog's own focus handling owns it.
  const escapeHatchRef = useRef<HTMLButtonElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (track === 'legal-floor') legalFirstRadio.current?.focus();
    else escapeHatchRef.current?.focus();
  }, [track]);

  // Derived preview content — computed once per render
  const isPost = target.kind === 'post';

  const previewBody = target.kind === 'post'
    ? truncate(bodyToPlainText(target.post.body), PREVIEW_MAX)
    : null;

  const noun = target.kind === 'post' ? 'post' : 'account';
  const plural = target.kind === 'post' ? 'content' : 'accounts';
  // Rewritten in 1.23.75. The old wording promised "a randomly-assembled Council
  // team will examine your report … you will receive a notification with the
  // outcome" — stated as a certainty, to every reporter, at the moment they are
  // most likely to believe it. What the platform actually guarantees is the part
  // that does not depend on a team being available: the post stays up, the
  // report is weighed against the five, one report on its own opens nothing, and
  // a decision — when there is one — is never silent.
  //
  // Deliberately does NOT promise "the real tally". build_explanation() in the
  // moderation pipeline takes no vote counts — it interpolates the THRESHOLD and
  // TEAM_SIZE constants, so every explanation reads the same ratio however the
  // vote actually went. Promising a real tally here would be a second false
  // sentence written to replace the first.
  const whatHappensNext = ` The ${noun} stays fully visible during review. TruXSocial does not hide ${plural} pre-decision. Your report is weighed against the five moderation categories; a single report on its own does not open a case, which is what stops a pile-on silencing someone. Any decision comes from a randomly-assembled Council team, with a written explanation.`;

  const title = legalSent
    ? 'Report received'
    : track === 'legal-floor'
      ? 'Report unlawful content'
      : isPost
        ? 'Report post'
        : 'Report account';

  /**
   * The identity of what is being reported, in the one field the server keeps.
   *
   * For a post the id also goes in `targetId`, which the server matches exactly
   * against `Post.legacy_id`; repeating it here — with the author's handle —
   * means a miss is still readable by the person who opens the report. For an
   * account there is no id the server can match at all — it only ever looks up a
   * post — so the handle in the text is the ONLY place the target survives.
   *
   * Everything this adds is named in the hint under the box, down to the values.
   * "We do not quietly append things to what someone wrote" is only true if the
   * disclosure lists what is actually appended, not a subset of it.
   */
  function detailWithTarget(body: string): string {
    const preamble =
      target.kind === 'post'
        ? `Reported post: ${target.post.handle} (id ${target.post.id})`
        : `Reported account: ${target.user.handle}`;
    return `${preamble}\n\n${body.trim()}`;
  }

  function openLegalFloor() {
    // Never mid-flight. A Council submit that lands after the switch would fire
    // onClose() underneath someone who is by then typing a legal-floor report —
    // destroying that text AND opening the case they had just decided against.
    if (submitting) return;
    // Drop the Council selection on the way out, so a category picked a moment
    // ago cannot be submitted down a track that never had it.
    setSelected(null);
    setCouncilFormError(null);
    setTrack('legal-floor');
  }

  function backToCouncil() {
    setLegalCategory(null);
    setLegalCategoryError(null);
    setLegalDetailError(null);
    setLegalFormError(null);
    setTrack('council');
  }

  async function handleSubmit() {
    if (!selected || submitting) return;
    setSubmitting(true);
    setCouncilFormError(null);
    try {
      if (target.kind === 'post') {
        await createCaseFromReport(target.post.id, selected, context.trim() || undefined);
      } else {
        await createAccountCaseFromReport(target.user.id, selected, context.trim() || undefined);
      }
      // Flat, and true. It used to say "the Council will review it", which is
      // the same promise the "What happens next" block just stopped making —
      // and it was the LAST thing a reporter read on the way out.
      toast('Report submitted.');
      onClose();
    } catch {
      // A rejected submit used to escape as an unhandled rejection: no toast,
      // no close, no message. The dialog stays open with the selection intact so
      // the reporter can simply press it again.
      setCouncilFormError(SEND_ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLegalFloorSubmit() {
    if (submitting) return;

    // Validated on submit, not by greying the button out: a control that is
    // inert with no stated reason is the dead-control pattern this repo keeps
    // removing, and it is a cruel one to meet on this particular form.
    const missingCategory = legalCategory === null;
    const missingDetail = legalDetail.trim().length === 0;
    setLegalCategoryError(missingCategory ? LEGAL_CATEGORY_ERROR : null);
    setLegalDetailError(missingDetail ? LEGAL_DETAIL_ERROR : null);
    setLegalFormError(null);
    if (missingCategory || missingDetail || legalCategory === null) {
      if (missingCategory) legalFirstRadio.current?.focus();
      else legalDetailField.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await legalFloorService.report({
        category: legalCategory,
        detail: detailWithTarget(legalDetail),
        // Only a post has an id the server can match; an account id would match
        // nothing and the server would accept the report anyway, so sending one
        // would only look like it worked.
        targetId: target.kind === 'post' ? target.post.id : '',
      });
      setLegalSent(true);
    } catch (err) {
      // Discriminate rather than flattening every failure into one line: a
      // rejected field is the reporter's to fix, a dropped connection is not.
      // And a field error is set ONLY for a field the server actually named —
      // blaming an unnamed one parks an error under a visibly-correct control
      // and swallows the one thing the reporter has to be told, which is that
      // the report did not go.
      const rejected =
        err instanceof ApiError && err.code === 'validation' ? (err.fields ?? {}) : {};
      const rejectedDetail = Boolean(rejected.detail);
      const rejectedCategory = Boolean(rejected.category);
      if (rejectedDetail) setLegalDetailError(LEGAL_DETAIL_ERROR);
      if (rejectedCategory) setLegalCategoryError(LEGAL_CATEGORY_ERROR);

      if (!rejectedDetail && !rejectedCategory) setLegalFormError(SEND_ERROR);
      else if (rejectedCategory) legalFirstRadio.current?.focus();
      else legalDetailField.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  function handleCategoryChange(e: ChangeEvent<HTMLInputElement>) {
    setSelected(e.currentTarget.value as ModerationCategory);
  }

  const targetPreview =
    target.kind === 'post' ? (
      <PostPreview>
        <PreviewAuthor>
          {target.post.name}
          <PreviewHandle>{target.post.handle}</PreviewHandle>
        </PreviewAuthor>
        <PreviewBody>{previewBody}</PreviewBody>
      </PostPreview>
    ) : (
      <AccountPreview>
        <Avatar name={target.user.name} size={40} />
        <AccountPreviewInfo>
          <AccountPreviewName>
            {target.user.name}
            {target.user.verified && (
              <Icon name="verify" size={14} title="Verified account" />
            )}
          </AccountPreviewName>
          <AccountPreviewHandle>{target.user.handle}</AccountPreviewHandle>
        </AccountPreviewInfo>
      </AccountPreview>
    );

  // ── The receipt ─────────────────────────────────────────────────────────────
  // Never the Council toast: "the Council will review it" is false on this
  // track, and a toast disappears — this is the one message the reporter most
  // needs to actually read.
  if (legalSent) {
    return (
      <Dialog open title={title} onClose={onClose}>
        <Receipt ref={receiptRef} tabIndex={-1} data-testid="legal-floor-receipt">
          {/* First, and in the same red the form gives it. This is the screen
              where someone is most likely to stop reading and assume it is
              handled, so the emergency line must not be the last thing here. */}
          <Callout $tone="urgent">
            If someone is in immediate danger, contact your local emergency services. We are not an
            emergency service and cannot act at that speed.
          </Callout>
          <Note>
            A person will read this. It does not go to a vote, and it is not judged by the Council.
            This route exists because there is nothing here to weigh. We cannot tell you how long
            that will take.
          </Note>
          <Note>
            We deliberately cannot show you a status page for it, and there is no reference number.
            This route is open to anyone, and a way to look a report up would let anyone check
            whether one exists and what happened to it.
          </Note>
          <ButtonRow>
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </ButtonRow>
        </Receipt>
      </Dialog>
    );
  }

  // ── The legal-floor track ───────────────────────────────────────────────────
  // The five are GONE from the document here, not merely deselected. Two lists
  // on screen at once is the thing this design exists to prevent.
  if (track === 'legal-floor') {
    return (
      // Not dismissable mid-send: a stray Escape or backdrop tap during the
      // request would close the dialog over a report that DID land, leaving the
      // reporter certain it never went — and no status page exists to check.
      <Dialog open title={title} onClose={onClose} dismissable={!submitting}>
        <Stack>
          {targetPreview}

          <Intro>
            This is for the small, closed list of material that is unlawful to host. It is handled
            outside the Council, it is never put to a vote, and one report is enough. The rules
            about reports having to build up exist to stop a pile-on silencing someone, and they
            have no place here.
          </Intro>
          <Callout $tone="urgent">
            If someone is in immediate danger, contact your local emergency services first. We
            cannot act at that speed.
          </Callout>

          {/* role="radiogroup" rather than a bare group: aria-invalid and
              aria-required are supported on a radiogroup and ignored on a group,
              so without it the box is announced as valid while the error is
              painted underneath. */}
          <div>
            <SectionLabel id="legal-floor-category-label">What are you reporting?</SectionLabel>
          </div>
          <RadioGroup
            role="radiogroup"
            aria-labelledby="legal-floor-category-label"
            aria-required="true"
            aria-invalid={legalCategoryError !== null}
            aria-describedby={legalCategoryError ? 'legal-floor-category-error' : undefined}
          >
            {LEGAL_FLOOR_CATEGORIES.map((c, i) => (
              <LegalRadioLabel key={c.id}>
                <RadioInput
                  ref={i === 0 ? legalFirstRadio : undefined}
                  type="radio"
                  name="legal-floor-category"
                  value={c.id}
                  checked={legalCategory === c.id}
                  onChange={() => {
                    setLegalCategory(c.id);
                    setLegalCategoryError(null);
                    // Rewriting the report drops the banner about the previous
                    // attempt — it describes a send that is no longer the one
                    // on screen.
                    setLegalFormError(null);
                  }}
                />
                <RadioText>
                  {c.label}
                  <RadioHint>{c.hint}</RadioHint>
                </RadioText>
              </LegalRadioLabel>
            ))}
          </RadioGroup>
          {legalCategoryError && (
            <FieldError id="legal-floor-category-error" role="alert">
              {legalCategoryError}
            </FieldError>
          )}

          {/* Required here, unlike the Council track's optional context box: the
              server rejects a blank one, and nobody can act on a report that
              says nothing. */}
          <Textarea
            ref={legalDetailField}
            label="What and where? (required)"
            placeholder="Describe what you found. Paste any links here."
            value={legalDetail}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setLegalDetail(e.currentTarget.value);
              if (legalDetailError) setLegalDetailError(null);
              if (legalFormError) setLegalFormError(null);
            }}
            rows={4}
            required
            maxLength={LEGAL_FLOOR_DETAIL_MAX}
            aria-invalid={legalDetailError !== null}
            /* All three, not a ternary. As a ternary, bouncing on the empty-box
               error silently dropped the "we will attach…" disclosure out of the
               accessible description — the exact quiet append this design exists
               to avoid — and the 4000-character stop was only ever visual. No
               aria-live: it would re-announce on every keystroke. */
            aria-describedby={[
              legalDetailError ? 'legal-floor-detail-error' : null,
              'legal-floor-target-hint',
              'legal-floor-detail-counter',
            ]
              .filter(Boolean)
              .join(' ')}
          />
          <Counter id="legal-floor-detail-counter">
            {legalDetail.length} / {LEGAL_FLOOR_DETAIL_MAX}
          </Counter>
          {legalDetailError && (
            <FieldError id="legal-floor-detail-error" role="alert">
              {legalDetailError}
            </FieldError>
          )}
          <FieldHint id="legal-floor-target-hint">
            {target.kind === 'post'
              ? `We will attach this post's id (${target.post.id}) and its author's handle (${target.post.handle}) so we can find it.`
              : `We will attach this account's handle (${target.user.handle}) so we know which one you mean. If a particular post is the problem, say which one above.`}
          </FieldHint>

          {legalFormError && (
            <FormError
              ref={legalFormErrorRef}
              tabIndex={-1}
              $tone="danger"
              role="alert"
              data-testid="legal-floor-error"
            >
              <Icon name="shield" size={16} title="Notice" />
              {legalFormError}
            </FormError>
          )}

          <ButtonRow>
            <Button variant="ghost" onClick={backToCouncil} disabled={submitting}>
              Back
            </Button>
            {/* NOT greyed out until valid — see handleLegalFloorSubmit. */}
            <Button variant="primary" onClick={handleLegalFloorSubmit} disabled={submitting}>
              {submitting ? 'Sending…' : 'Send report'}
            </Button>
          </ButtonRow>
        </Stack>
      </Dialog>
    );
  }

  // ── The Council track (what opens) ──────────────────────────────────────────
  return (
    <Dialog open title={title} onClose={onClose} dismissable={!submitting}>
      <Stack>
        {/* Target preview */}
        {targetPreview}

        {/* Category selection — exactly five, keyboard-accessible native radios */}
        <div>
          <SectionLabel id="report-category-label">Reason for report</SectionLabel>
        </div>
        <RadioGroup role="radiogroup" aria-labelledby="report-category-label">
          {MODERATION_CATEGORIES.map((cat) => (
            <RadioLabel key={cat}>
              <RadioInput
                type="radio"
                name="report-category"
                value={cat}
                checked={selected === cat}
                onChange={handleCategoryChange}
              />
              {CATEGORY_LABELS[cat]}
            </RadioLabel>
          ))}
        </RadioGroup>

        {/* The way out of the five. A button, never a sixth radio: picking it
            changes where the report GOES, and the two lists must not sit
            together as though a team could choose between them. Worded without
            naming the Council, because the block below names it once and that
            sentence is the one members are pinned to. */}
        <EscapeHatch>
          <EscapeText>
            Some material is unlawful for anyone to host: sexual content involving a child, intimate
            images shared without the person&rsquo;s consent, and content covered by a valid legal
            order. None of the five cover it. It is not weighed, judged or voted on (a vote cannot
            make something lawful), so it goes down a separate, non-discretionary route where one
            report is enough.
          </EscapeText>
          <Button
            ref={escapeHatchRef}
            variant="outline"
            size="sm"
            onClick={openLegalFloor}
            disabled={submitting}
          >
            Report unlawful content instead
          </Button>
        </EscapeHatch>

        {/* Optional context */}
        <Textarea
          label="Context (optional)"
          placeholder="Add context (optional)"
          value={context}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setContext(e.currentTarget.value)}
          rows={3}
        />

        {/* Decorative — evidence upload (visibly inert). The "Mute this user, coming
            soon" row that sat beside it is gone: Mute is real and lives in the post
            and profile menus, so calling it "coming soon" here was simply false.
            TruXSocial is mute-only by design — there is no Block row here either. */}
        <DecorativeSection aria-hidden="true">
          <DecorativeRow>
            <Icon name="image" size={16} />
            Attach evidence
            <DecorativeBadge>coming soon</DecorativeBadge>
          </DecorativeRow>
        </DecorativeSection>

        {/* "What happens next" — free-speech framing */}
        <InfoBox data-testid="what-happens-next">
          <Icon name="hourglass" size={16} title="Info" />
          <InfoText>
            <strong>What happens next:</strong>{whatHappensNext}
          </InfoText>
        </InfoBox>

        {councilFormError && (
          <FormError
            ref={councilFormErrorRef}
            tabIndex={-1}
            $tone="danger"
            role="alert"
            data-testid="report-error"
          >
            <Icon name="shield" size={16} title="Notice" />
            {councilFormError}
          </FormError>
        )}

        {/* Actions */}
        <ButtonRow>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selected === null || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </Button>
        </ButtonRow>
      </Stack>
    </Dialog>
  );
}
